import {
  getPlatformColor,
  getPlatformIcon,
} from "@/components/channel/platform-utils";
import { proxyImageUrl } from "@/lib/image-url";
import { PageHeader } from "@/components/layout/header-provider";
import { OrganizationAvatar } from "@/components/organization/organization-avatar";
import { ManualFilter } from "@/components/shadcn-studio/blocks/datatable-account";
import { calculatePagination } from "@/hooks/use-pagination";
import { useDebounce } from "@/lib/hooks/use-debounce";
import type { Platform } from "@/lib/queries/channel";
import { api as trpc, trpcClient } from "@/lib/trpc";
import {
  Badge,
  Button,
  Card,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Mail,
  MailX,
  MoreHorizontal,
  RotateCcw,
  Search,
  ShieldX,
  Trash2,
  TriangleAlert,
  User,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const ALLOWED_VERIFIED = ["true", "false"] as const;
const ALLOWED_ACCOUNT_TYPES = ["creator", "agency", "soft_deleted"] as const;

type VerifiedFilter = (typeof ALLOWED_VERIFIED)[number];
type AccountTypeFilter = (typeof ALLOWED_ACCOUNT_TYPES)[number];

export const Route = createFileRoute("/admin/profiles")({
  validateSearch: (
    search,
  ): {
    page: number;
    q: string | undefined;
    verified: VerifiedFilter | undefined;
    accountType: AccountTypeFilter | undefined;
    includeDeleted: boolean;
  } => {
    const rawPage = Number(search.page);
    const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;

    const q =
      typeof search.q === "string" && search.q.length > 0
        ? search.q
        : undefined;

    const rawVerified = search.verified;
    const verified = (ALLOWED_VERIFIED as readonly string[]).includes(
      rawVerified as string,
    )
      ? (rawVerified as VerifiedFilter)
      : undefined;

    const rawAccountType = search.accountType;
    const accountType = (ALLOWED_ACCOUNT_TYPES as readonly string[]).includes(
      rawAccountType as string,
    )
      ? (rawAccountType as AccountTypeFilter)
      : undefined;

    const includeDeleted = search.includeDeleted === true;

    return { page, q, verified, accountType, includeDeleted };
  },
  component: ProfilesPage,
});

function ProfilesPage() {
  const { page, q, verified, accountType, includeDeleted } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const setPage = (newPage: number) =>
    navigate({ search: (prev) => ({ ...prev, page: newPage }) });

  const [search, setSearch] = useState(q ?? "");
  const debouncedSearch = useDebounce(search, 500);
  const queryClient = useQueryClient();

  // Sync debounced search value to URL (drives the query via `q` param)
  useEffect(() => {
    const normalized = debouncedSearch || undefined;
    if (normalized !== q) {
      navigate({
        search: (prev) => ({ ...prev, q: normalized, page: 1 }),
        replace: true,
      });
    }
  }, [debouncedSearch, q, navigate]);

  const [unverifyTarget, setUnverifyTarget] = useState<{
    profileId: string;
    email: string;
  } | null>(null);

  const [restoreTarget, setRestoreTarget] = useState<{
    profileId: string;
    name: string;
  } | null>(null);
  const [restoreConflictEmail, setRestoreConflictEmail] = useState<
    string | null
  >(null);

  const { data, isLoading, isError, error } = useQuery(
    trpc.admin.getProfiles.queryOptions(
      {
        page,
        limit: 25,
        search: debouncedSearch || undefined,
        verified: verified,
        accountType: accountType,
        includeDeleted,
      },
      {
        placeholderData: (prev) => prev,
      },
    ),
  );

  const { mutate: unverifyEmail, isPending: isUnverifying } = useMutation({
    mutationFn: (vars: { profileId: string }) =>
      trpcClient.admin.unverifyProfileEmail.mutate(vars),
    onSuccess: (result) => {
      toast.success(`Email ${result.email} has been unverified`);
      setUnverifyTarget(null);
      queryClient.invalidateQueries({
        queryKey: trpc.admin.getProfiles.queryKey(),
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const { mutate: sendVerification, isPending: isSendingVerification } =
    useMutation({
      mutationFn: (vars: { profileId: string }) =>
        trpcClient.admin.sendProfileVerification.mutate(vars),
      onSuccess: (result) => {
        toast.success(`Verification email sent to ${result.email}`);
        queryClient.invalidateQueries({
          queryKey: trpc.admin.getProfiles.queryKey(),
        });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    });

  const { mutate: softDeleteProfile } = useMutation({
    mutationFn: (vars: { profileId: string }) =>
      trpcClient.admin.softDeleteProfile.mutate(vars),
    onSuccess: (result) => {
      toast.success(`Profile "${result.name}" has been soft-deleted`);
      queryClient.invalidateQueries({
        queryKey: trpc.admin.getProfiles.queryKey(),
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const { mutate: restoreProfile, isPending: isRestoringProfile } = useMutation(
    {
      mutationFn: (vars: { profileId: string; force?: boolean }) =>
        trpcClient.admin.restoreProfile.mutate(vars),
      onSuccess: (result) => {
        if (result.clearedVerification) {
          toast.success(
            `Profile "${result.name}" restored. Email verification was cleared due to a conflict.`,
          );
        } else {
          toast.success(`Profile "${result.name}" has been restored`);
        }
        setRestoreTarget(null);
        setRestoreConflictEmail(null);
        queryClient.invalidateQueries({
          queryKey: trpc.admin.getProfiles.queryKey(),
        });
      },
      onError: (err) => {
        if (err.message.includes("conflict") && restoreTarget) {
          const emailMatch = err.message.match(/\[([^\]]+)\]/);
          setRestoreConflictEmail(emailMatch ? emailMatch[1] : null);
        } else {
          toast.error(err.message);
        }
      },
    },
  );

  const totalPages = data?.meta.totalPages ?? 0;
  const total = data?.meta.total ?? 0;
  const limit = 25;

  const { pages, showLeftEllipsis, showRightEllipsis } = calculatePagination({
    currentPage: page,
    totalPages,
    paginationItemsToDisplay: 5,
  });

  return (
    <div className="flex flex-col gap-6 p-6 max-md:p-0 h-full">
      <PageHeader>
        <span className="font-semibold">Profiles</span>
      </PageHeader>

      <Card className="flex-1 flex flex-col overflow-hidden max-md:border-0 max-md:shadow-none max-md:bg-transparent max-md:rounded-none">
        <div className="p-4 border-b flex flex-col md:flex-row items-center gap-4 justify-between bg-card text-card-foreground">
          <div className="flex items-center gap-2 w-full md:flex-1 md:max-w-sm relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email, name, or slug..."
              className="pl-9"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="w-full md:w-45">
              <ManualFilter
                label="Verified"
                value={verified ?? "all"}
                onValueChange={(val) => {
                  navigate({
                    search: (prev) => ({
                      ...prev,
                      verified:
                        val === "all" ? undefined : (val as VerifiedFilter),
                      page: 1,
                    }),
                  });
                }}
                options={[
                  { value: "true", label: "Verified" },
                  { value: "false", label: "Unverified" },
                ]}
              />
            </div>
            <div className="w-full md:w-50">
              <ManualFilter
                label="Account"
                value={accountType ?? "all"}
                onValueChange={(val) => {
                  navigate({
                    search: (prev) => ({
                      ...prev,
                      accountType:
                        val === "all" ? undefined : (val as AccountTypeFilter),
                      page: 1,
                    }),
                  });
                }}
                options={[
                  { value: "creator", label: "Creator" },
                  { value: "agency", label: "Agency" },
                  { value: "soft_deleted", label: "Soft Deleted" },
                ]}
              />
            </div>
            <Button
              variant={includeDeleted ? "default" : "outline"}
              size="sm"
              className="whitespace-nowrap"
              onClick={() => {
                navigate({
                  search: (prev) => ({
                    ...prev,
                    includeDeleted: !includeDeleted,
                    page: 1,
                  }),
                });
              }}
            >
              <Trash2 className="size-3.5 mr-1.5" />
              Deleted
            </Button>
            {(q || verified || accountType || includeDeleted) && (
              <Button
                variant="ghost"
                size="sm"
                className="whitespace-nowrap"
                onClick={() => {
                  setSearch("");
                  navigate({
                    search: {
                      page: 1,
                      q: undefined,
                      verified: undefined,
                      accountType: undefined,
                      includeDeleted: false,
                    },
                  });
                }}
              >
                <X className="size-3.5 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="h-14">
                <TableHead className="pl-4">Profile</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Channels</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data.length ? (
                data.data.map((profile) => (
                  <TableRow
                    key={profile.id}
                    className={`h-14 ${profile.deletedAt ? "opacity-50" : ""}`}
                  >
                    <TableCell className="pl-4">
                      <div className="flex items-center gap-3">
                        <div className="size-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                          {profile.avatarUrl ? (
                            <img
                              src={proxyImageUrl(profile.avatarUrl)}
                              alt={profile.name}
                              className="size-8 rounded-full object-cover"
                            />
                          ) : (
                            <User className="size-4 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium">{profile.name}</span>
                            {profile.deletedAt && (
                              <Badge
                                variant="destructive"
                                className="text-[10px] px-1 py-0"
                              >
                                Deleted
                              </Badge>
                            )}
                          </div>
                          <span className="text-muted-foreground text-xs">
                            {profile.slug}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {profile.email ?? "\u2014"}
                    </TableCell>
                    <TableCell>
                      {profile.emailVerified ? (
                        <Badge variant="default" className="text-xs">
                          <Mail className="size-3 mr-1" />
                          Verified
                        </Badge>
                      ) : profile.email ? (
                        <Badge variant="secondary" className="text-xs">
                          <MailX className="size-3 mr-1" />
                          Unverified
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {profile.emailSource ?? "\u2014"}
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/o/$orgSlug"
                        params={{ orgSlug: profile.orgSlug }}
                        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                      >
                        {profile.orgDeletedAt ? (
                          <div className="relative rounded-md bg-red-50 dark:bg-red-950/30 p-0.5">
                            <OrganizationAvatar
                              organization={{
                                logo: profile.orgLogo,
                                name: profile.orgName,
                                metadata: profile.orgMetadata,
                              }}
                              className="size-8 ring-2 ring-destructive/60 opacity-70"
                              showBadge={false}
                            />
                            <div className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-red-500 border border-white dark:border-background size-4">
                              <TriangleAlert className="size-2.5 text-white" />
                            </div>
                          </div>
                        ) : (
                          <OrganizationAvatar
                            organization={{
                              logo: profile.orgLogo,
                              name: profile.orgName,
                              metadata: profile.orgMetadata,
                            }}
                            className="size-8 ring-2 ring-background"
                          />
                        )}
                        <div className="flex flex-col">
                          <span className="text-sm">{profile.orgName}</span>
                          <span className="text-muted-foreground text-xs underline decoration-muted-foreground/30">
                            {profile.orgSlug}
                          </span>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {profile.channels.map((platform) => {
                          const Icon = getPlatformIcon(platform as Platform);
                          const color = getPlatformColor(platform as Platform);
                          return (
                            <div
                              key={platform}
                              className="flex items-center justify-center size-6 rounded-md bg-muted"
                              title={platform}
                            >
                              <Icon className="size-3.5" style={{ color }} />
                            </div>
                          );
                        })}
                        {profile.channels.length === 0 && (
                          <span className="text-muted-foreground text-xs">
                            {"\u2014"}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(profile.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right pr-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 p-0"
                            aria-label="Actions"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!profile.emailVerified &&
                            profile.email &&
                            !profile.deletedAt && (
                              <DropdownMenuItem
                                disabled={isSendingVerification}
                                onClick={() =>
                                  sendVerification({
                                    profileId: profile.id,
                                  })
                                }
                              >
                                <Mail className="mr-2 h-4 w-4" />
                                Send Verification
                              </DropdownMenuItem>
                            )}
                          {profile.emailVerified && profile.email && (
                            <DropdownMenuItem
                              onClick={() => {
                                const email = profile.email;
                                if (email)
                                  setUnverifyTarget({
                                    profileId: profile.id,
                                    email,
                                  });
                              }}
                            >
                              <ShieldX className="mr-2 h-4 w-4" />
                              Unverify Email
                            </DropdownMenuItem>
                          )}
                          {profile.deletedAt ? (
                            <DropdownMenuItem
                              onClick={() =>
                                setRestoreTarget({
                                  profileId: profile.id,
                                  name: profile.name,
                                })
                              }
                            >
                              <RotateCcw className="mr-2 h-4 w-4" />
                              Restore Profile
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() =>
                                softDeleteProfile({
                                  profileId: profile.id,
                                })
                              }
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Soft Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {isLoading
                      ? "Loading..."
                      : isError
                        ? `Error: ${error?.message ?? "Failed to load profiles"}`
                        : "No profiles found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t max-sm:flex-col md:max-lg:flex-col">
          <p
            className="text-muted-foreground text-sm whitespace-nowrap"
            aria-live="polite"
          >
            Showing{" "}
            <span>
              {total === 0 ? 0 : (page - 1) * limit + 1} to{" "}
              {total === 0 ? 0 : Math.min(page * limit, total)}
            </span>{" "}
            of <span>{total} entries</span>
          </p>

          <div>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <Button
                    className="disabled:pointer-events-none disabled:opacity-50"
                    variant="ghost"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    aria-label="Go to previous page"
                  >
                    <ChevronLeftIcon aria-hidden="true" />
                    Previous
                  </Button>
                </PaginationItem>

                {showLeftEllipsis && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {pages.map((p) => {
                  const isActive = p === page;
                  return (
                    <PaginationItem key={p}>
                      <Button
                        size="icon"
                        className={cn(
                          !isActive &&
                            "bg-primary/10 text-primary hover:bg-primary/20 focus-visible:ring-primary/20 dark:focus-visible:ring-primary/40",
                        )}
                        onClick={() => setPage(p)}
                        aria-current={isActive ? "page" : undefined}
                      >
                        {p}
                      </Button>
                    </PaginationItem>
                  );
                })}

                {showRightEllipsis && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                <PaginationItem>
                  <Button
                    className="disabled:pointer-events-none disabled:opacity-50"
                    variant="ghost"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages}
                    aria-label="Go to next page"
                  >
                    Next
                    <ChevronRightIcon aria-hidden="true" />
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </Card>

      <Dialog
        open={unverifyTarget !== null}
        onOpenChange={(open) => {
          if (!open) setUnverifyTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unverify Email</DialogTitle>
            <DialogDescription>
              This will clear the email verification for{" "}
              <strong>{unverifyTarget?.email}</strong>. The email address will
              be preserved, but the profile will need to re-verify it. This
              frees up the email for another profile to claim.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUnverifyTarget(null)}
              disabled={isUnverifying}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (unverifyTarget) {
                  unverifyEmail({ profileId: unverifyTarget.profileId });
                }
              }}
              disabled={isUnverifying}
            >
              {isUnverifying ? "Unverifying..." : "Unverify Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={restoreTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRestoreTarget(null);
            setRestoreConflictEmail(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {restoreConflictEmail
                ? "Email Conflict Detected"
                : "Restore Profile"}
            </DialogTitle>
            <DialogDescription>
              {restoreConflictEmail ? (
                <>
                  The email <strong>{restoreConflictEmail}</strong> is already
                  verified by another active profile. Forcing restore will clear
                  the verification on this profile — the email will be preserved
                  but will need to be re-verified.
                </>
              ) : (
                <>
                  This will restore the profile{" "}
                  <strong>{restoreTarget?.name}</strong>. It will become active
                  again.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRestoreTarget(null);
                setRestoreConflictEmail(null);
              }}
              disabled={isRestoringProfile}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (restoreTarget) {
                  restoreProfile({
                    profileId: restoreTarget.profileId,
                    force: restoreConflictEmail !== null,
                  });
                }
              }}
              disabled={isRestoringProfile}
            >
              {isRestoringProfile
                ? "Restoring..."
                : restoreConflictEmail
                  ? "Force Restore"
                  : "Restore Profile"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
